/**
 * Centralized Logger Utility
 * 
 * Provides configurable logging with different levels:
 * - DEBUG: Detailed information for debugging (only in dev or with ?debug=true)
 * - INFO: General informational messages
 * - WARN: Warning messages (potential issues)
 * - ERROR: Error messages (always shown)
 * 
 * Usage:
 *   import { logger } from './logger'
 *   logger.debug('Debug message', data)
 *   logger.info('Info message', data)
 *   logger.warn('Warning message', data)
 *   logger.error('Error message', error)
 * 
 * Enable debug logs in production: Add ?debug=true to URL
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
}

class Logger {
  constructor() {
    this.level = null // Will be set on first use
  }

  updateLogLevel() {
    // Check URL parameter for debug mode
    const urlParams = new URLSearchParams(window.location.search)
    const debugParam = urlParams.get('debug')
    
    // Determine log level based on priority:
    // 1. URL parameter (?debug=true) - highest priority
    // 2. Environment detection (hostname-based)
    // 3. Default (production = errors only)
    
    if (debugParam === 'true') {
      this.level = LOG_LEVELS.DEBUG
      console.log('🔍 Debug mode enabled via URL parameter')
    } else {
      // Detect environment from hostname to avoid circular dependency
      const hostname = window.location.hostname
      const isLocal = hostname.includes('localhost') || hostname.includes('127.0.0.1')
      const isDev = hostname.includes('staging') || hostname.includes('dev')
      
      if (isLocal || isDev) {
        // Development/Staging: Show debug and info
        this.level = LOG_LEVELS.NONE // For now, show warnings and errors. Will change to DEBUG later.
      } else {
        // Production: Only errors and warnings
        this.level = LOG_LEVELS.NONE // For now, show nothing. Will change to WARN later.
      }
    }
  }
  
  ensureInitialized() {
    if (this.level === null) {
      this.updateLogLevel()
    }
  }

  /**
   * Debug level logging - detailed information for debugging
   * Only shown in development or with ?debug=true
   */
  debug(...args) {
    this.ensureInitialized()
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.log(...args)
    }
  }

  /**
   * Info level logging - general informational messages
   * Shown in development and staging
   */
  info(...args) {
    this.ensureInitialized()
    if (this.level <= LOG_LEVELS.INFO) {
      console.log(...args)
    }
  }

  /**
   * Warning level logging - potential issues
   * Shown in all environments
   */
  warn(...args) {
    this.ensureInitialized()
    if (this.level <= LOG_LEVELS.WARN) {
      console.warn(...args)
    }
  }

  /**
   * Error level logging - errors and exceptions
   * Always shown in all environments
   */
  error(...args) {
    this.ensureInitialized()
    if (this.level <= LOG_LEVELS.ERROR) {
      console.error(...args)
    }
  }

  /**
   * Group logging - for collapsible log groups
   */
  group(label) {
    this.ensureInitialized()
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.group(label)
    }
  }

  groupEnd() {
    this.ensureInitialized()
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.groupEnd()
    }
  }

  /**
   * Table logging - for structured data
   */
  table(data) {
    this.ensureInitialized()
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.table(data)
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export LOG_LEVELS for reference
export { LOG_LEVELS }
